import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  DocumentPreviewView,
  HelpTip,
  Icon,
  PdfViewer,
  cn,
  formatDateOnly,
  fileIndicatorsOf,
  dateSourceLabel,
  useToast,
} from '@dataroom/shared';
import type { ClassificationProposal, RoomDocument } from '@dataroom/shared';
import {
  useApproveProposal,
  useModifyDocumentAttributes,
  usePendingProposals,
  useReclassifyDocument,
  useRejectProposal,
  useRoomCategories,
  useRoomDocuments,
  useRoomFolderOrder,
} from '@/api/hooks';
import { ClassificationStatusBadge } from '@/components/ClassificationStatusBadge';
import { DocumentDetailModal } from '@/components/DocumentDetailModal';
import { VersionSuggestionsPanel } from '@/components/VersionSuggestionsPanel';
import { DocumentVersionHistory } from '@/components/DocumentVersionHistory';
import { isUnauthorized } from '@/lib/checklist';
import {
  categoryNodesById,
  categoryParents,
  categoryPathsById,
  deepestCategoryId,
} from '@dataroom/shared';
import { FilingLevel } from '@/components/FilingTrail';
import { NewCategoryHere } from '@/components/NewCategoryHere';
import {
  filingTrail,
  idsWhenChoosing,
  levelNumber,
  offersNewCategoryAt,
  roundFrom,
  trailLabel,
  type LevelView,
} from '@/lib/filing-trail';
import {
  leftToFile,
  pickReviewDocument,
  reviewQueue,
  reviewRound,
  suggestionLabel,
  type ReviewOption,
} from '@/lib/classification-review';
import { useDocumentPreview } from '@dataroom/shared';
import { useRoomContext } from './context';

function pct(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function errorText(err: unknown): string | null {
  if (!err) return null;
  return err instanceof ApiError ? err.message : 'That didn’t save. Try again.';
}

/** Keys typed into a field, or pressed with a modifier, or while a dialog is
 * open, are never queue shortcuts. Buttons/links keep Enter and Space. */
function ignoreShortcut(e: KeyboardEvent): boolean {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return true;
  const t = e.target as HTMLElement | null;
  if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return true;
  if (e.key === 'Enter' && t && /^(BUTTON|A)$/.test(t.tagName)) return true;
  return document.querySelector('[aria-modal="true"]') !== null;
}

/**
 * The full-page classification review queue (route /rooms/:roomId/review).
 * Every document the AI filed with low confidence or could not file, one at a
 * time: the file itself on the left (in-app viewer), the AI's ranked
 * suggestions on the right. One click — or Enter — accepts the top
 * suggestion; any other choice is one click (or its number key). When a
 * document is fully filed it drops out and the next document that STILL needs
 * filing opens on its own — chosen from the re-fetched listing, so one the AI
 * (or someone else) filed meanwhile is skipped; when none is left the page
 * returns to the Data room list saying so. The header counts what is left to
 * file. `?doc=<id>` opens the queue at that document (or, if it needs no
 * filing, at the next one after it).
 *
 * Every choice files through the same category PATCH the review panel uses (the
 * server records it as the person's decision, approving the suggestion it took
 * and rejecting the ones it superseded); period/date suggestions go through the
 * proposal approve/reject endpoints. The per-document panel stays one click
 * away ("More options…") for re-filing a wrong branch.
 */
export function RoomReviewPage() {
  const { roomId } = useRoomContext();
  const qc = useQueryClient();
  const documents = useRoomDocuments(roomId);
  const categories = useRoomCategories(roomId);
  const folderOrder = useRoomFolderOrder(roomId);
  const pending = usePendingProposals(roomId);
  const modify = useModifyDocumentAttributes(roomId);
  const approve = useApproveProposal(roomId);
  const reject = useRejectProposal(roomId);
  const reclassify = useReclassifyDocument(roomId);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [currentId, setCurrentId] = useState<string | null>(params.get('doc'));
  // The shown document's position in the listing, for when it leaves it.
  const anchor = useRef(0);
  // Whether this visit has shown a document (an empty queue then means "done").
  const shownAny = useRef(false);
  // True from an action until the listing and the suggestions have refreshed,
  // so a second press never acts on the stale round.
  const [settling, setSettling] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  // The level being chosen: the deepest filed category's children, or a level
  // the person stepped back up to with the breadcrumb (nothing saved until a
  // choice). And whether "+ New category here" has its name field open.
  const [view, setView] = useState<LevelView>({ kind: 'filed' });
  const [newOpen, setNewOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const tree = useMemo(() => categories.data ?? [], [categories.data]);
  const parentOf = useMemo(() => categoryParents(tree), [tree]);
  const paths = useMemo(() => categoryPathsById(tree), [tree]);
  const byId = useMemo(() => categoryNodesById(tree), [tree]);
  const names = useMemo(
    () => new Map([...byId].map(([id, node]) => [id, node.name] as const)),
    [byId],
  );
  const phases = folderOrder.data?.phases;
  const listing = useMemo(() => documents.data ?? [], [documents.data]);
  const queue = useMemo(
    () => reviewQueue(listing, pending.data ?? [], currentId),
    [listing, pending.data, currentId],
  );
  const left = leftToFile(listing);
  // Until an action's re-fetch has landed, stay on the document acted on; then
  // choose the next one from the fresh listing (never a stale position).
  const holding = settling ? (listing.find((d) => d.id === currentId) ?? null) : null;
  const loaded = !documents.isPending && !pending.isPending;
  const current = loaded
    ? (holding ?? pickReviewDocument(listing, queue, currentId, anchor.current))
    : null;
  const index = current ? queue.findIndex((d) => d.id === current.id) : -1;

  // Follow the auto-advance: remember where we are, and pin the id we show.
  useEffect(() => {
    if (!current) return;
    shownAny.current = true;
    const at = listing.findIndex((d) => d.id === current.id);
    if (at !== -1) anchor.current = at;
    if (current.id !== currentId) setCurrentId(current.id);
  }, [current, currentId, listing]);

  // Nothing left to file after working through the queue: back to the list.
  const allFiled = loaded && !settling && !documents.error && current === null;
  useEffect(() => {
    if (!allFiled || !shownAny.current) return;
    toast({ message: 'All documents are filed' });
    navigate(`/rooms/${roomId}`, { replace: true });
  }, [allFiled, navigate, roomId, toast]);

  // A new document: move focus to its heading so keyboard and screen-reader
  // users land on it (the button they pressed is gone).
  const shownId = current?.id ?? null;
  const firstShown = useRef(true);
  useEffect(() => {
    // Each document starts at its own deepest filed level.
    setView({ kind: 'filed' });
    setNewOpen(false);
    if (firstShown.current) {
      firstShown.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [shownId]);

  const docProposals = useMemo(
    () =>
      current
        ? (pending.data ?? [])
            .filter((p) => p.documentId === current.id)
            .sort((a, b) => b.confidence - a.confidence)
        : [],
    [pending.data, current],
  );
  const round = current
    ? reviewRound(current, tree, parentOf, docProposals, roundFrom(view, phases))
    : null;
  const deepest = current ? deepestCategoryId(current.categoryTagIds, parentOf) : null;
  const offersNew = round !== null && offersNewCategoryAt(round.parentId, byId, parentOf);
  const offered = new Set(round?.options.map((o) => o.id) ?? []);
  const others = docProposals.filter((p) => !offered.has(p.tagId));
  const top = round?.options[0]?.suggestion ? round.options[0] : null;

  const busy =
    settling || modify.isPending || approve.isPending || reject.isPending || reclassify.isPending;
  const actionError =
    errorText(modify.error) ??
    errorText(approve.error) ??
    errorText(reject.error) ??
    errorText(reclassify.error);

  const resetErrors = useCallback(() => {
    modify.reset();
    approve.reset();
    reject.reset();
    reclassify.reset();
  }, [modify, approve, reject, reclassify]);

  /** Run one action, then wait for the listing + suggestions to refresh.
   * Resolves true when the action succeeded. */
  const run = useCallback(
    async (action: () => Promise<unknown>): Promise<boolean> => {
      resetErrors();
      setSettling(true);
      try {
        await action();
        await Promise.all([
          qc.refetchQueries({ queryKey: ['rooms', roomId, 'documents'], exact: true }),
          qc.refetchQueries({ queryKey: ['rooms', roomId, 'classification', 'pending'] }),
        ]);
        return true;
      } catch {
        // Shown through the mutation's own error below.
        return false;
      } finally {
        setSettling(false);
      }
    },
    [qc, roomId, resetErrors],
  );

  const levelParentId = round?.parentId ?? null;
  const fileUnder = useCallback(
    (option: Pick<ReviewOption, 'id'>) => {
      if (!current || busy) return;
      const ids = idsWhenChoosing(
        current.categoryTagIds ?? [],
        deepest,
        parentOf,
        levelParentId,
        option.id,
      );
      setNewOpen(false);
      void run(() =>
        modify.mutateAsync({ documentId: current.id, body: { categoryTagIds: ids } }),
      ).then((ok) => {
        // Filed: the next level continues from where it now is.
        if (ok) setView({ kind: 'filed' });
      });
    },
    [current, busy, run, modify, deepest, parentOf, levelParentId],
  );

  const go = useCallback(
    (delta: number) => {
      if (queue.length === 0 || index < 0) return;
      const next = queue[(index + delta + queue.length) % queue.length];
      if (next) {
        resetErrors();
        setCurrentId(next.id);
      }
    },
    [queue, index, resetErrors],
  );

  // Keyboard: Enter / A accept the top suggestion, 1–9 choose that option,
  // N a new category at this level, J next, K previous. (←/→ and +/−/0 stay
  // the PDF viewer's page and zoom.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ignoreShortcut(e)) return;
      const key = e.key.toLowerCase();
      let handled = true;
      if ((key === 'enter' || key === 'a') && top) fileUnder(top);
      else if (/^[1-9]$/.test(key) && round?.options[Number(key) - 1]) {
        fileUnder(round.options[Number(key) - 1]!);
      } else if (key === 'n' && offersNew && !busy) setNewOpen(true);
      else if (key === 'j') go(1);
      else if (key === 'k') go(-1);
      else handled = false;
      if (handled) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [top, round, fileUnder, go, offersNew, busy]);

  if (isUnauthorized(documents.error)) return <Navigate to="/login" replace />;

  const backLink = (
    <Link
      to={`/rooms/${roomId}`}
      className="inline-flex w-fit items-center gap-1.5 text-[13px] text-ink-2 hover:underline"
    >
      <Icon name="arrowLeft" size={14} />
      Back to list
    </Link>
  );

  if (documents.isPending || categories.isPending || pending.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (documents.error) {
    return <p className="text-sm text-destructive">{documents.error.message}</p>;
  }

  if (!current) {
    return (
      <div className="space-y-4">
        {backLink}
        <Card>
          <CardContent className="space-y-2 py-10 text-center">
            <Icon name="check" size={20} className="mx-auto text-st-certified-fg" />
            <p className="font-semibold">All documents are filed</p>
            <p className="text-sm text-muted-foreground">
              Nothing is left to file. New uploads that need a look will show up here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filedPath = deepest ? trailLabel(deepest, parentOf, names, phases) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          {backLink}
          <p className="text-lg font-semibold">Review queue</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="rounded-pill border bg-surface-2 px-3 py-1 text-sm font-semibold tabular-nums"
            role="status"
            aria-live="polite"
          >
            {left} left to file
          </span>
          <HelpTip id="admin.review.queue" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(-1)}
            disabled={queue.length < 2}
            aria-label="Previous document"
            title="Previous (K)"
          >
            <Icon name="chevronLeft" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(1)}
            disabled={queue.length < 2}
            aria-label="Skip to the next document"
            title="Next (J)"
          >
            <Icon name="chevronRight" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)]">
        <Card className="overflow-hidden">
          {current.downloadReady ? (
            <ReviewPreview key={current.id} roomId={roomId} document={current} />
          ) : (
            <div className="flex h-[40vh] items-center justify-center p-6 text-center text-sm text-muted-foreground">
              This file isn’t available to view yet.
            </div>
          )}
        </Card>

        <Card className="h-fit lg:sticky lg:top-20">
          <CardContent className="space-y-5 pt-5 text-sm">
            <div className="space-y-2">
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="break-words text-base font-semibold focus:outline-none"
              >
                {current.name}
              </h2>
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
                {current.classificationStatus || current.processingStatus ? (
                  <ClassificationStatusBadge
                    status={current.classificationStatus}
                    processingStatus={current.processingStatus}
                    indicators={fileIndicatorsOf(current)}
                    paperworkKind={current.paperworkKind}
                    inFolder={current.categoryPath.length > 0}
                  />
                ) : null}
                <span>{filedPath ? `Filed so far: ${filedPath}` : 'Not filed yet'}</span>
              </div>
            </div>

            {pending.isPending ? (
              <p className="text-muted-foreground">Loading suggestions…</p>
            ) : round ? (
              <FilingLevel
                crumbs={filingTrail(
                  {
                    parentId: round.parentId,
                    phaseId: view.kind === 'phase' ? view.phaseId : null,
                  },
                  parentOf,
                  names,
                  phases,
                )}
                level={levelNumber(round.parentId, parentOf)}
                onStep={(next) => {
                  setNewOpen(false);
                  setView(next);
                }}
                onReturn={
                  view.kind === 'filed'
                    ? undefined
                    : {
                        label: (deepest ? names.get(deepest) : null) ?? 'where it is filed',
                        go: () => {
                          setNewOpen(false);
                          setView({ kind: 'filed' });
                        },
                      }
                }
              >
                <SuggestionList round={round} busy={busy} onChoose={fileUnder} />
                {offersNew ? (
                  <NewCategoryHere
                    key={`${current.id}:${round.parentId ?? 'top'}`}
                    roomId={roomId}
                    categories={tree}
                    parentId={round.parentId}
                    parentName={round.parentId ? (names.get(round.parentId) ?? null) : null}
                    open={newOpen}
                    onOpenChange={setNewOpen}
                    disabled={busy}
                    shortcut
                    onChosen={(category) => fileUnder(category)}
                  />
                ) : null}
              </FilingLevel>
            ) : (
              <p className="text-muted-foreground">
                There is no further level to choose here. Use “More options…” to file it in a
                different branch.
              </p>
            )}

            {others.length > 0 ? (
              <OtherSuggestions
                proposals={others}
                paths={paths}
                busy={busy}
                onApprove={(id) => void run(() => approve.mutateAsync(id))}
                onReject={(id) => void run(() => reject.mutateAsync(id))}
              />
            ) : null}

            {actionError ? (
              <p className="text-sm text-destructive" role="alert">
                {actionError}
              </p>
            ) : null}

            {/* The last filing's series (same name, other dates) is being filed
                alike in the background (learning/series.ts). */}
            {similarNote(modify.data?.similarDocuments) ? (
              <p className="text-sm text-muted-foreground" role="status">
                {similarNote(modify.data?.similarDocuments)}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2 border-t pt-4">
              {current.aiClassifiable !== false ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void run(() => reclassify.mutateAsync(current.id))}
                  title="Re-run the AI on this file. Categories you set yourself are kept."
                >
                  <Icon name="refresh" size={14} />
                  Re-run AI
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  resetErrors();
                  setDetailOpen(true);
                }}
              >
                More options…
              </Button>
            </div>

            <p className="text-xs text-muted-foreground" aria-hidden="true">
              <Kbd>Enter</Kbd> accept the top suggestion · <Kbd>1</Kbd>–<Kbd>9</Kbd> choose ·{' '}
              {offersNew ? (
                <>
                  <Kbd>N</Kbd> new category ·{' '}
                </>
              ) : null}
              <Kbd>J</Kbd> next · <Kbd>K</Kbd> previous
            </p>
            <p className="sr-only">
              Keyboard: Enter accepts the top suggestion, number keys 1 to 9 choose a category,
              {offersNew ? ' N adds a new category at this level,' : ''} J goes to the next document
              and K to the previous one.
            </p>
          </CardContent>
        </Card>
      </div>

      {detailOpen ? (
        <DocumentDetailModal
          key={current.id}
          document={current}
          categories={tree}
          phases={phases}
          proposals={pending.data ?? []}
          proposalsLoading={pending.isPending}
          approvingId={approve.isPending ? (approve.variables ?? null) : null}
          rejectingId={reject.isPending ? (reject.variables ?? null) : null}
          reclassifying={reclassify.isPending}
          savingCategories={modify.isPending}
          actionError={actionError}
          onApprove={(id) => void run(() => approve.mutateAsync(id))}
          onReject={(id) => void run(() => reject.mutateAsync(id))}
          onReclassify={() => void run(() => reclassify.mutateAsync(current.id))}
          onSetCategories={(categoryTagIds, onSaved) =>
            void run(() =>
              modify.mutateAsync({ documentId: current.id, body: { categoryTagIds } }),
            ).then((ok) => {
              if (ok) onSaved();
            })
          }
          onOpenPreview={() => setDetailOpen(false)}
          versionSuggestions={<VersionSuggestionsPanel roomId={roomId} documentId={current.id} />}
          versionHistory={
            current.downloadReady ? (
              <DocumentVersionHistory roomId={roomId} documentId={current.id} />
            ) : null
          }
          onClose={() => setDetailOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="rounded border bg-surface-2 px-1 font-sans text-[11px] font-semibold text-ink-2">
      {children}
    </kbd>
  );
}

/** The AI's ranked choices for the level — suggested first. The top
 * suggestion is the primary action; every option is one click. */
function SuggestionList({
  round,
  busy,
  onChoose,
}: {
  round: NonNullable<ReturnType<typeof reviewRound>>;
  busy: boolean;
  onChoose: (option: ReviewOption) => void;
}) {
  return (
    <div>
      <ol className="mt-2 space-y-1.5" aria-label="AI suggestions, best first">
        {round.options.map((option, i) => {
          const isTop = i === 0 && option.suggestion !== null;
          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onChoose(option)}
                aria-label={`File under ${option.name}${option.suggestion ? `, AI ${pct(option.suggestion.confidence)}` : ''}`}
                aria-keyshortcuts={i < 9 ? String(i + 1) : undefined}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
                  isTop
                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary-hover'
                    : 'bg-surface hover:bg-surface-2',
                )}
              >
                {i < 9 ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-4 shrink-0 text-center text-xs font-semibold tabular-nums',
                      isTop ? 'text-primary-foreground/80' : 'text-ink-3',
                    )}
                  >
                    {i + 1}
                  </span>
                ) : null}
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{option.name}</span>
                  {option.hasChildren ? (
                    <span
                      className={cn(
                        'block text-xs',
                        isTop ? 'text-primary-foreground/80' : 'text-muted-foreground',
                      )}
                    >
                      More levels below
                    </span>
                  ) : null}
                </span>
                {option.suggestion ? (
                  <span
                    className={cn(
                      'shrink-0 rounded-pill px-2 py-0.5 text-xs font-semibold tabular-nums',
                      isTop ? 'bg-primary-foreground/15' : 'bg-st-check text-st-check-fg',
                    )}
                  >
                    AI {pct(option.suggestion.confidence)}
                  </span>
                ) : null}
                {isTop ? (
                  <span className="shrink-0 text-xs font-semibold" aria-hidden="true">
                    Accept ↵
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
      {round.options[0]?.suggestion ? null : (
        <p className="mt-2 text-xs text-muted-foreground">
          The AI has no suggestion at this level — choose where it goes.
        </p>
      )}
    </div>
  );
}

/** Period / date / document-type suggestions: accept or reject each. */
function OtherSuggestions({
  proposals,
  paths,
  busy,
  onApprove,
  onReject,
}: {
  proposals: ClassificationProposal[];
  paths: ReadonlyMap<string, string>;
  busy: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  return (
    <div>
      <p className="font-medium">Also suggested</p>
      <ul className="mt-2 space-y-1.5">
        {proposals.map((p) => {
          const label = suggestionLabel(p, paths);
          return (
            <li
              key={p.id}
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block font-medium">{label}</span>
                <span className="text-xs text-muted-foreground">
                  {/* A date read from the file name shows where, not a percentage. */}
                  {dateSourceLabel(p) ? `Read ${dateSourceLabel(p)}` : `AI ${pct(p.confidence)}`}
                </span>
              </span>
              <span className="flex shrink-0 gap-1.5">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => onApprove(p.id)}
                  aria-label={`Accept ${label}`}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onReject(p.id)}
                  aria-label={`Reject ${label}`}
                >
                  Reject
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The document itself, inline (no dialog): PDF in the in-app viewer, images,
 * text, office files from the server-built preview (ADR-0033), or a "no
 * preview" note for other types. */
function ReviewPreview({ roomId, document }: { roomId: string; document: RoomDocument }) {
  const { content, error, loading } = useDocumentPreview(roomId, document.id, document.name);
  const meta = [document.period, document.dataDate ? formatDateOnly(document.dataDate) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="flex h-[70vh] min-h-[420px] flex-col">
      {meta ? (
        <p className="border-b px-4 py-2 text-xs text-ink-2">
          <span className="sr-only">Period and date: </span>
          {meta}
        </p>
      ) : null}
      {content?.kind === 'pdf' && content.data ? (
        <PdfViewer data={content.data} title={document.name} className="min-h-0 flex-1" />
      ) : content?.kind === 'office' &&
        content.preview &&
        content.preview.kind !== 'unsupported' ? (
        <DocumentPreviewView
          preview={content.preview}
          title={document.name}
          className="min-h-0 flex-1"
        />
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/30 p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading document…</p>
          ) : error ? (
            <p className="max-w-md text-center text-sm text-destructive">{error}</p>
          ) : content?.kind === 'image' ? (
            <img
              src={content.url}
              alt={document.name}
              className="max-h-full max-w-full object-contain"
            />
          ) : content?.kind === 'text' ? (
            <pre className="h-full w-full self-stretch overflow-auto whitespace-pre-wrap break-words rounded border bg-background p-4 text-xs leading-relaxed">
              {content.text}
            </pre>
          ) : (
            <p className="max-w-md text-center text-sm text-muted-foreground">
              Preview isn’t available for this file type. Its name and the AI’s reading of it are on
              the right.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** "Also filing 23 similar documents" — the series the last filing is applied to. */
function similarNote(similar: { filing: number; conflicts: number } | undefined): string | null {
  if (!similar || (similar.filing === 0 && similar.conflicts === 0)) return null;
  const parts: string[] = [];
  if (similar.filing > 0) {
    parts.push(
      `Also filing ${similar.filing} similar document${similar.filing === 1 ? '' : 's'} (same name, other dates) the same way.`,
    );
  }
  if (similar.conflicts > 0) {
    parts.push(
      `${similar.conflicts} similar document${similar.conflicts === 1 ? ' is' : 's are'} already filed elsewhere and left as they are.`,
    );
  }
  return parts.join(' ');
}
